import { IHierarchyService } from "../../../hierarchy/common/hierarchy.js";
import { IInspectionService } from "../../../inspection/common/inspection.js";
import { IInstantiationService } from "../../../instantiation/common/instantiation.js";
import { ICodeViewService } from "../../../code/common/codeView.js";
import { INodeGraphInspectionService } from "../../../webgl/three.js/nodeGraphInspectionService.js";
import { AIChatAnchor, AIContextProviderCapabilities, AITextContextChunk, IAIContextProvider } from "../ai.js";
import { labelOfTarget } from "../targetLabel.js";
import { aiTargetForElement, resolveAiTarget, resolveAnchorSubject, type AiTarget, type TargetResolverLike } from "../target.js";
import { displayComponentName } from "../../../webgl/three.js/componentName.js";
import { IObjectTargetService } from "../../../../editor/services/project/common/documentTarget.js";
import { IMeshBakeGroupService } from "../../../../editor/services/meshBaker/common/bakeGroups.js";
import { safeStringify } from "../../../../base/common/objects.js";

/**
 * Tells the AI what the user currently SEES in the editor — the current
 * selection, the last "inspect this" request, the code panel's source, and the
 * node-graph panel's drill-down level — so it can pass the right ids/kinds to
 * `code_read`, `nodegraph_read` and the property tools without guessing.
 * Everything is serialized to short strings at request time; nothing live is
 * retained (weak-registry rule). Re-sent per chat message, so it stays lean.
 */
/**
 * A property value in one short line: enough to answer "what is it set to",
 * never a dump. Three-ish objects are summarised by what they ARE (a vector's
 * numbers, a colour's hex) rather than serialised — a texture or a material
 * would otherwise drag its whole graph into the prompt.
 */
function describeValue(value: unknown): string {
    if (value === null) return "null";
    switch (typeof value) {
        case "undefined": return "undefined";
        case "string": return value.length > 80 ? `"${value.slice(0, 80)}…" (${value.length} chars)` : `"${value}"`;
        case "number":
        case "boolean":
        case "bigint": return String(value);
        case "function": return "(a function)";
        case "symbol": return "(a symbol)";
    }
    if (Array.isArray(value)) {
        if (value.length > 8) return `an array of ${value.length}`;
        return `[${value.map(entry => describeValue(entry)).join(", ")}]`;
    }
    const duck = value as {
        isVector3?: boolean; isVector2?: boolean; isEuler?: boolean; isQuaternion?: boolean;
        isColor?: boolean; getHexString?: () => string;
        x?: number; y?: number; z?: number; w?: number;
        type?: string; name?: string; uuid?: string;
    };
    if (duck.isColor && typeof duck.getHexString === "function") return `#${duck.getHexString()}`;
    if (duck.isVector2) return `(${duck.x}, ${duck.y})`;
    if (duck.isVector3 || duck.isEuler) return `(${duck.x}, ${duck.y}, ${duck.z})`;
    if (duck.isQuaternion) return `(${duck.x}, ${duck.y}, ${duck.z}, ${duck.w})`;
    // an object with an addressable identity says what it is, not what it holds
    const kind = duck.type || (value as { constructor?: { name?: string } }).constructor?.name || "object";
    const named = duck.name ? ` "${duck.name}"` : "";
    return `a ${kind}${named}${duck.uuid ? ` (uuid ${duck.uuid})` : ""}`;
}

/** rounded for the prompt — the raycast's 15 decimals are noise, not precision */
function fmt(v: { x: number; y: number; z: number }): string {
    const r = (n: number) => Number(n.toFixed(3));
    return `(${r(v.x)}, ${r(v.y)}, ${r(v.z)})`;
}

/**
 * Where the context click LANDED — for an anchor opened from the 3D viewport.
 * The local point is the stored truth; the CURRENT world position is recomputed
 * from it through the live matrixWorld (plain column-major math, no three
 * import), so the description says where the point IS and only brings up where
 * it WAS when the object has moved since the click.
 */
function hitLines(anchor: AIChatAnchor, resolved: object): string[] {
    const hit = anchor.hit;
    if (!hit) return [];
    const lines = [
        `- the chat was opened by a click in the 3D viewport; the hit point is ${fmt(hit.local)} in the object's LOCAL space`
        + (hit.parent ? ` (the object is parented under "${hit.parent}")` : ""),
    ];
    const e = (resolved as { matrixWorld?: { elements?: unknown } }).matrixWorld?.elements;
    const m = Array.isArray(e) && e.length === 16 && e.every(n => typeof n === "number") ? e as number[] : null;
    if (m) {
        const { x, y, z } = hit.local;
        const current = {
            x: m[0] * x + m[4] * y + m[8] * z + m[12],
            y: m[1] * x + m[5] * y + m[9] * z + m[13],
            z: m[2] * x + m[6] * y + m[10] * z + m[14],
        };
        lines.push(`- that point is at ${fmt(current)} in WORLD space right now`);
        const drift = Math.hypot(current.x - hit.world.x, current.y - hit.world.y, current.z - hit.world.z);
        if (drift > 1e-3) {
            lines.push(`- the object has moved since: at the time of the click the point was at ${fmt(hit.world)}`);
        }
    } else {
        lines.push(`- at the time of the click that point was at ${fmt(hit.world)} in WORLD space`);
    }
    return lines;
}

export class AIEditorStateContext implements IAIContextProvider {
    readonly name = "editor-state";
    readonly capabilities: AIContextProviderCapabilities = AIContextProviderCapabilities.Text;

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @IInstantiationService private readonly instantiation: IInstantiationService,
    ) { }

    // Optional services (not every product registers them) — resolved lazily
    // per request, same pattern as ReadNodeGraphTool._renderGraphs.
    private get _inspection(): IInspectionService | null {
        try { return this.instantiation.invokeFunction(a => a.get(IInspectionService)); } catch { return null; }
    }
    private get _codeView(): ICodeViewService | null {
        try { return this.instantiation.invokeFunction(a => a.get(ICodeViewService)); } catch { return null; }
    }
    private get _nodeGraphs(): INodeGraphInspectionService | null {
        try { return this.instantiation.invokeFunction(a => a.get(INodeGraphInspectionService)); } catch { return null; }
    }
    private get _targets(): TargetResolverLike | undefined {
        try { return this.instantiation.invokeFunction(a => a.get(IObjectTargetService)) as TargetResolverLike; } catch { return undefined; }
    }
    private get _bakeGroups(): IMeshBakeGroupService | null {
        try { return this.instantiation.invokeFunction(a => a.get(IMeshBakeGroupService)); } catch { return null; }
    }

    /** the document target for a pointed-at object — element identity is MINTED
     *  on first request (the shared rule lives in {@link aiTargetForElement}) */
    private mintFor(object: object): AiTarget | null {
        return aiTargetForElement(this._targets, this.hierarchy, object);
    }

    /**
     * Describe an anchored conversation's anchor — called EVERY turn, so the
     * description is live, and it must come back in every case: resolved (the
     * object, duck-typed — no engine import), gone ("not in the scene"), or
     * ambiguous (resolveAiTarget fails loud on shared ids by design; for an
     * anchor that is a state to REPORT, never a reason to kill the turn).
     */
    /**
     * Objects and the properties reached through them. Other kinds (a
     * resource, a file, a graph node) are somebody else's provider to answer —
     * this one describes what it can resolve in the scene graph.
     */
    async getAnchorContext(anchor: AIChatAnchor): Promise<AITextContextChunk[] | null> {
        let resolved: object | null;
        try {
            // BOTH rungs: a resource's own address dies with the session that
            // minted it, and `via` names the place it hangs instead
            resolved = resolveAnchorSubject(this._targets, this.hierarchy, anchor);
        } catch (e) {
            return [{ type: "text", text: `The anchor is currently AMBIGUOUS: ${e instanceof Error ? e.message : e}` }];
        }
        if (!resolved) {
            return [{ type: "text", text: "The anchor does not resolve right now — it may have been removed or not loaded yet. Say so if asked about it; the tools can search for it by name." }];
        }

        // duck-typed three-ish description — absent facts are simply not lines
        const object = resolved as {
            type?: string; isMesh?: boolean;
            children?: unknown[];
            position?: { x: number; y: number; z: number };
            material?: { name?: string } | Array<{ name?: string }>;
            userData?: { components?: object[] };
        };
        const lines: string[] = [];
        if (typeof object.type === "string") lines.push(`- a ${object.type}`);
        if (Array.isArray(object.children)) lines.push(`- ${object.children.length} children`);
        const components = object.userData?.components;
        if (Array.isArray(components) && components.length) {
            lines.push(`- components: ${components.map(c => displayComponentName(c)).join(", ")}`);
        }
        const materials = Array.isArray(object.material) ? object.material : object.material ? [object.material] : [];
        if (materials.length) {
            lines.push(`- material${materials.length > 1 ? "s" : ""}: ${materials.map(m => m.name || "(unnamed)").join(", ")}`);
        }
        const p = object.position;
        if (p && typeof p.x === "number") lines.push(`- position: (${p.x}, ${p.y}, ${p.z})`);
        lines.push(...hitLines(anchor, resolved));

        // A PROPERTY anchor: the object above is where it lives, the property
        // is the subject. Read FRESH like everything else here — a value from
        // when the chat opened would describe a property that has since moved.
        const property = anchor.property;
        if (property) {
            const home = property.ownerType
                ? `the \`${property.key}\` property of the ${property.ownerType} on this object`
                : `the \`${property.key}\` property of this object`;
            const owner = resolved as Record<string, unknown>;
            // Only readable when it lives on the ADDRESSED object: a material or
            // geometry row anchors at the mesh, and guessing which sub-object
            // to read would invent an answer.
            const readable = !property.ownerType && property.key in owner;
            const value = readable
                ? describeValue(owner[property.key])
                : property.ownerType
                    ? "(not readable from here — use the tools to read the sub-object)"
                    : "(no longer present on this object)";
            return [{
                type: "text",
                text: [`The subject is ${home}.`, `- current value: ${value}`, "It lives on:", ...lines].join("\n"),
            }];
        }

        return [{ type: "text", text: lines.join("\n") }];
    }

    async getContext(): Promise<AITextContextChunk[] | null> {
        const lines: string[] = ["Editor state (what the user currently sees):"];

        // A pending inspect request (Resources panel click, object-reference
        // link) IS the user's current focus — it outranks the hierarchy
        // selection as the base target, so it comes FIRST and says so ("this
        // mesh" in a question means the inspected thing, not the selection).
        // Transient read — the target is never retained.
        // Name it, and hand over the token that REACHES it. Without a target the
        // line describes something the tools cannot be pointed at, and the model
        // goes hunting: one session spent six calls looking for "resource 1009" —
        // which was not an id at all but a texture's texel-type constant, printed
        // as its identity (see labelOfTarget).
        const label = (target: object) => {
            const minted = this.mintFor(target);
            return minted ? `${labelOfTarget(target)} (target ${safeStringify(minted)})` : labelOfTarget(target);
        };
        // A reveal (e.g. clicking a resource in the node graph opened its texture
        // in the preview) never becomes the base, so the code panel / code_read
        // stay put — but it IS the user's LATEST pointing gesture, so the
        // resource-oriented read tools now resolve to it (getFocusTargets).
        const reveal = this._inspection?.getLastReveal();
        const request = this._inspection?.getCurrentTarget();
        if (reveal) {
            lines.push(`- POINTING AT: ${label(reveal.target)} (revealed via ${reveal.source}) — the user's latest `
                + "gesture. No-arg property_find, property_read and nodegraph_read target THIS ('this', 'the one I "
                + "clicked'). (code_read still reads the code panel / base below.)");
        }
        if (request) {
            lines.push(`- INSPECTING: ${label(request.target)} (via ${request.source}) — the current base focus. `
                + "No-arg code_read reads this"
                + (reveal ? "" : ", as do property_find, property_read and nodegraph_read")
                + " (not the hierarchy selection).");
        }

        // Selection — the no-args tool default only when nothing is inspected.
        const selection = this.hierarchy?.getSelectedNodes() ?? [];
        lines.push(selection.length
            ? `- Selected in hierarchy: ${selection.slice(0, 10).map(n => {
                const minted = n.owner && typeof n.owner === "object" ? this.mintFor(n.owner) : null;
                return `${n.name || n.type}${minted ? ` (target ${safeStringify(minted)})` : ""}`;
            }).join(", ")}`
            + (selection.length > 10 ? ` … +${selection.length - 10} more` : "")
            + (request ? " (the inspected target above takes precedence)" : "")
            : "- Selected: nothing");

        // Code panel: which source is on screen + what else the target offers.
        const codeView = this._codeView;
        if (codeView?.options.length) {
            const selected = codeView.selected;
            const others = codeView.options
                .filter(o => o.id !== selected?.id)
                .slice(0, 12)
                .map(o => o.kind)
                .join(", ");
            // A project FILE on screen routes to the file tools — no-arg
            // code_read follows the INSPECTION, so promising it here sent the
            // model in circles when an inspection overrode the panel (live
            // 2026-08-31: main.ts on screen, code_read answered with the
            // inspected Mesh).
            const filePath = selected?.kind === "file-source"
                ? (selected as { file?: { path?: string } }).file?.path
                : undefined;
            lines.push("- Code panel: showing "
                + (selected ? `"${selected.group ? `${selected.group} — ` : ""}${selected.label}" (kind ${selected.kind})` : "nothing")
                + (codeView.locked ? " [locked]" : "")
                + (others ? `; other sources on this target: ${others}` : "")
                + (filePath
                    ? `. It is the project file '${filePath}' — read it with project_readFile, edit it with project_editFile (path '${filePath}'); project_listFiles / project_searchFiles cover the other project files.`
                    : ". Call code_read with NO arguments to read exactly this."));
        }

        // Bake groups that already EXIST — without this line the model cannot
        // know and re-discovers the meshes into a twin group ("do it again"
        // live case, 2026-08-29).
        const bakeGroups = this._bakeGroups?.groups;
        if (bakeGroups?.length) {
            const listed = bakeGroups.slice(0, 8)
                .map(g => `"${g.name}" (groupId ${g.id}, ${g.members.length} meshes)`)
                .join(", ");
            lines.push(`- Existing bake groups: ${listed}`
                + (bakeGroups.length > 8 ? ` … +${bakeGroups.length - 8} more` : "")
                + ". Re-bake one with webgl_optimizeBakeGroup — do NOT create a new group for the same meshes.");
        }

        // Node graph panel: the drill-down level the user is looking at.
        const nav = this._nodeGraphs?.navigation;
        if (nav?.graphs.length) {
            const graphs = nav.graphs.slice(0, 8).map(g => `${g.title} (graphId ${g.id})`).join(", ");
            lines.push(`- Node graph panel: ${nav.path.join(" > ")} — showing ${graphs}`
                + (nav.graphs.length > 8 ? ` … +${nav.graphs.length - 8} more` : "")
                + ". Call nodegraph_read / code_read with NO arguments to read what's on screen.");
        }

        return [{ type: "text", text: lines.join("\n") }];
    }
}
